import { Dispatch, SetStateAction, use, useCallback, useContext, useEffect, useState } from "react";
import { usePrevious } from '@react-hooks-library/core';

import { useUserApiMutation } from "src/lib/filmwebid/useUserApiMutation";
import { useUserContext } from "src/lib/contexts/UserContext";
import { useQueryClient } from "@tanstack/react-query";
import { buildUserApiQueryKey, useUserApiQueryKey } from "src/lib/filmwebid/useUserApiQuery";
import { emptyMovieActivity, IMovieActivity, IUserDataForMovie } from "src/lib/filmwebid/IMovieActivity";
import { useMovieActivityContext } from "src/components/film/filmwebid/MovieActivityContext";
import { useMovieActivityOptimisticUpdater } from "src/lib/filmwebid/movieActivityMutationOptimisticUpdater";

type UseWatchListArgs = {
	edi?: string | null;
	streamingId?: number | null;
	onComplete?: () => void;
	initialValue?: boolean | null;
}

export default function useWatchlist({ edi, streamingId, onComplete, initialValue }: UseWatchListArgs): [(value: boolean | null) => void, boolean] {

	const { updateWatchlistIdList } = useUserContext();

	const queryKey = useUserApiQueryKey("/api/Movie/MovieActivity", { edi, streamingId });
	
	const optimisticUpdater = useMovieActivityOptimisticUpdater(queryKey);

	const { mutate, isPending } = useUserApiMutation("/api/Watchlist", { allowAnon: false }, {
		onMutate: (variables: { method: string }) =>  optimisticUpdater(old => {
			const newWatchlistedValue = variables.method === "POST" ? true : false;
			return {
				...old,
				movieUserInteraction: {
					...(old?.movieUserInteraction ?? emptyMovieActivity.movieUserInteraction!),
					watchlisted: newWatchlistedValue
				},
				movieStats: {
					...(old?.movieStats ?? emptyMovieActivity.movieStats!),
					watchlistCount: newWatchlistedValue ? (old?.movieStats?.watchlistCount ?? 0) + 1 : (old?.movieStats?.watchlistCount ?? 0) - 1
				}
			}
		}),
		onSuccess: onComplete
	});
	const setWatchlist = useCallback((value: boolean | null) => {
		updateWatchlistIdList({ edi, streamingId }, /* remove = */ !value);
		if (value) {
			mutate({
				method: "POST",
				body: { EDI: edi ?? null, streamingId: streamingId ?? null, source: window?.location?.hostname ?? null }
			});
		} else {
			mutate({
				method: "DELETE",
				body: { edi: edi ?? null, streamingId: streamingId ?? null }
			});
		}
	}, [edi, mutate, streamingId, updateWatchlistIdList]);

	return [setWatchlist, isPending];
}